import React from "react";
import { Box, CircularProgress } from "@mui/joy";
import IconButtonCTA from "../common/IconButtonCTA";
import { SSave } from "../../styles/StyledMUI";
import dayjs from "dayjs";
import { useAlert } from "../../contexts/app/notificationToast";
import type { PortalResponse } from "../../api/portal/Portal.typed";
import type { TrainingCourse } from "../../api/training/Training.typed";
import { useAuth, usePortal } from "../../contexts/api";
import UserHeader from "./parts/UserHeader";
import ProfileCard from "./parts/ProfileCard";
import TrainingsList from "./parts/TrainingsList";
import { zekayStaticBranding } from "../../config/staticFiles";
import {
  portalDockedActionsBarSx,
  roundActionIconButtonSx,
  textTokens,
  colorTokens,
  withAlpha,
} from "../../styles/globalStyles";
import {
  DOCUMENT_MAX_BYTES,
  DOCUMENT_MAX_EDGE,
  DOCUMENT_QUALITY,
  PHOTO_MAX_BYTES,
  PHOTO_MAX_EDGE,
  PHOTO_QUALITY,
  compressImageIfNeeded,
} from "../../utils/fileSaver";

function hasBirthdate(value: string | null | undefined): boolean {
  if (!value) return false;
  const day = dayjs(value);
  if (!day.isValid()) return false;
  // Go zero time and legacy portal clear sentinel.
  if (day.year() <= 1900) return false;
  return true;
}

function normalizeBirthdate(value: string | null | undefined): string | null {
  return hasBirthdate(value) ? value! : null;
}

export default function PortalPage() {
  const [customer, setCustomer] = React.useState<PortalResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isFetching, setIsFetching] = React.useState<boolean>(true);
  const [openTrainings, setOpenTrainings] = React.useState<Record<number, boolean>>({});
  const [street, setStreet] = React.useState<string | undefined>("");
  const [postalCode, setPostalCode] = React.useState<string | undefined>("");
  const [city, setCity] = React.useState<string | undefined>("");
  const [birthdate, setBirthdate] = React.useState<string | null | undefined>("");
  const [photo, setPhoto] = React.useState<string | File | undefined>(undefined);
  const [medical, setMedical] = React.useState<File | string | undefined>(undefined);
  const [disclaimer, setDisclaimer] = React.useState<File | string | undefined>(undefined);
  const [agreement, setAgreement] = React.useState<File | string | undefined>(undefined);
  const [phonenumber, setPhonenumber] = React.useState<string>("");
  const [gdpr, setGdpr] = React.useState<boolean>(false);
  const { logout } = useAuth();
  const {
    getPortalCustomerInfo,
    updatePortalCustomerInfo,
    getPublicTheoryTestTemplate,
    getTrainingTemplateById,
  } = usePortal();
  const { showAlert } = useAlert();
  const [testAvailability, setTestAvailability] = React.useState<Record<number, boolean>>({});
  const [docsByTemplateId, setDocsByTemplateId] = React.useState<
    Record<
      number,
      { TrainingCourses?: TrainingCourse[]; MedicalAgreement?: string; MedicalDisclaimer?: string }
    >
  >({});

  const trainings = customer?.Trainings || [];

  const logoSrc = customer?.LogoUrl || zekayStaticBranding.zekayIconPng();

  React.useEffect(() => {
    async function fetchCustomer() {
      setIsFetching(true);
      try {
        const data = await getPortalCustomerInfo();
        if (data) {
          setCustomer(data);
        }
      } catch (err) {
        logout();
        showAlert("Session expirée ou lien invalide", "danger");
      } finally {
        setIsFetching(false);
      }
    }
    fetchCustomer();
  }, [getPortalCustomerInfo, logout, showAlert]);

  React.useEffect(() => {
    if (customer) {
      setStreet(customer.Street || "");
      setPostalCode(customer.PostalCode || "");
      setCity(customer.City || "");
      setBirthdate(normalizeBirthdate(customer.Birthdate));
      setPhonenumber(customer.Phonenumber || "");
      setPhoto(typeof customer.Photo === "string" ? customer.Photo : undefined);
      setMedical(customer.MedicalCertificate);
      setGdpr(customer.GdprConsent);
    }
  }, [customer]);

  const toggleTrainingOpen = async (idx?: number, trainingTemplateId?: number) => {
    const row = idx === undefined || idx < 0 ? 0 : idx;
    setOpenTrainings((prev) => ({ ...prev, [row]: !prev[row] }));
    if (!trainingTemplateId || trainingTemplateId <= 0) return;
    if (testAvailability[trainingTemplateId] === undefined) {
      try {
        const template = await getPublicTheoryTestTemplate(String(trainingTemplateId));
        setTestAvailability((prev) => ({ ...prev, [trainingTemplateId]: Boolean(template) }));
      } catch {
        setTestAvailability((prev) => ({ ...prev, [trainingTemplateId]: false }));
      }
    }
    if (!docsByTemplateId[trainingTemplateId]) {
      const tpl = await getTrainingTemplateById(String(trainingTemplateId));
      setDocsByTemplateId((prev) => ({
        ...prev,
        [trainingTemplateId!]: {
          TrainingCourses: tpl?.TrainingCourses,
          MedicalAgreement: tpl?.MedicalAgreement,
          MedicalDisclaimer: tpl?.MedicalDisclaimer,
        },
      }));
    }
  };

  const handleSave = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("Street", street || "");
      formData.append("PostalCode", postalCode || "");
      formData.append("City", city || "");
      formData.append(
        "Birthdate",
        hasBirthdate(birthdate) ? dayjs(birthdate).format("YYYY-MM-DD") + "T00:00:00Z" : "",
      );
      formData.append("Phonenumber", phonenumber);
      formData.append("GdprConsent", String(gdpr));

      if (photo instanceof File) {
        const compressedPhoto = await compressImageIfNeeded(
          photo,
          PHOTO_MAX_EDGE,
          PHOTO_MAX_EDGE,
          PHOTO_QUALITY,
          PHOTO_MAX_BYTES,
        );
        formData.append("Photo", compressedPhoto);
      }
      if (medical instanceof File) {
        const compressedMedical = await compressImageIfNeeded(
          medical,
          DOCUMENT_MAX_EDGE,
          DOCUMENT_MAX_EDGE,
          DOCUMENT_QUALITY,
          DOCUMENT_MAX_BYTES,
        );
        formData.append("MedicalCertificate", compressedMedical);
      }
      if (agreement instanceof File) {
        const compressedAgreement = await compressImageIfNeeded(
          agreement,
          DOCUMENT_MAX_EDGE,
          DOCUMENT_MAX_EDGE,
          DOCUMENT_QUALITY,
          DOCUMENT_MAX_BYTES,
        );
        formData.append("MedicalAgreement", compressedAgreement);
      }
      if (disclaimer instanceof File) {
        const compressedDisclaimer = await compressImageIfNeeded(
          disclaimer,
          DOCUMENT_MAX_EDGE,
          DOCUMENT_MAX_EDGE,
          DOCUMENT_QUALITY,
          DOCUMENT_MAX_BYTES,
        );
        formData.append("MedicalDisclaimer", compressedDisclaimer);
      }

      await updatePortalCustomerInfo(formData);
      showAlert("Informations envoyées avec succès !", "success");
    } catch {
      // Alert / logout already handled in portal context.
    } finally {
      setIsLoading(false);
    }
  };
  const handleSignOff = () => {
    logout();
  };

  if (isFetching) {
    return (
      <Box
        sx={{
          backgroundColor: "black",
          color: textTokens.color.primary,
          fontFamily: textTokens.fontFamily,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: textTokens.color.primary }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "black",
        color: textTokens.color.primary,
        fontFamily: textTokens.fontFamily,
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          pt: { xs: 2, sm: 2.5 },
          pb: { xs: 1.5, sm: 2 },
          px: { xs: 1.5, sm: 2 },
        }}
      >
        <Box
          component="img"
          src={logoSrc}
          alt="Logo de l'entreprise"
          sx={{
            display: "block",
            objectFit: "contain",
            objectPosition: "center top",
            width: "auto",
            height: "auto",
            maxWidth: { xs: "min(95vw, 280px)", sm: 300 },
            maxHeight: { xs: "min(46vh, 300px)", sm: 280 },
          }}
        />
      </Box>

      {!isFetching && customer && <UserHeader onLogout={handleSignOff} />}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 3,
          py: 2,
          pb: { xs: 10, sm: 9 },
        }}
      >
        <ProfileCard
          firstName={customer?.FirstName}
          lastName={customer?.LastName}
          street={street}
          setStreet={setStreet}
          postalCode={postalCode}
          setPostalCode={setPostalCode}
          city={city}
          setCity={setCity}
          birthdate={birthdate}
          setBirthdate={setBirthdate}
          photo={photo}
          setPhoto={setPhoto}
          medical={medical}
          setMedical={setMedical}
          phonenumber={phonenumber}
          setPhonenumber={setPhonenumber}
          gdpr={gdpr}
          setGdpr={setGdpr}
        />

        {trainings.length > 0 && (
          <TrainingsList
            customer={customer}
            trainings={trainings}
            openTrainings={openTrainings}
            toggleTrainingOpen={toggleTrainingOpen}
            docsByTemplateId={docsByTemplateId}
            testAvailability={testAvailability}
            disclaimer={disclaimer}
            setDisclaimer={setDisclaimer}
            agreement={agreement}
            setAgreement={setAgreement}
          />
        )}
      </Box>

      {!isFetching && (
        <Box sx={portalDockedActionsBarSx}>
          <IconButtonCTA
            type="button"
            onClick={() => {
              const active = document.activeElement;
              if (active instanceof HTMLElement) {
                active.blur();
              }
              void handleSave();
            }}
            disabled={isLoading}
            ctaVariant="success"
            aria-label="Enregistrer le profil"
            sx={roundActionIconButtonSx}
          >
            <SSave iconColor="inherit" />
          </IconButtonCTA>
        </Box>
      )}

      {isLoading && (
        <Box
          role="progressbar"
          aria-label="Enregistrement en cours"
          aria-busy="true"
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: withAlpha(colorTokens.bg.overlay, 0.55),
            pointerEvents: "all",
            touchAction: "none",
          }}
        >
          <CircularProgress size="lg" sx={{ color: textTokens.color.title }} />
        </Box>
      )}
    </Box>
  );
}
